/**
 * Contextual ad-targeting vocabulary (Wave 4c, plan `check-all-claude-gentle-
 * valiant.md` T1). Dependency-free on purpose: the client context builder
 * (`ad-context-client.ts`), the zod settings schema and the request-path
 * sampler all read it, and the client must never pull `zod` in.
 *
 * Every dimension value is a closed set. The server re-validates each value
 * against these lists and maps anything else to `unknown`, so a hand-edited
 * query string can never mint a new context cell.
 */

export const TARGETING_DIMENSIONS = [
	"device",
	"traffic",
	"funnel",
	"page_family",
	"market",
	"segment",
] as const;

export type TargetingDimension = (typeof TARGETING_DIMENSIONS)[number];

export const DIMENSION_VALUES: Record<TargetingDimension, readonly string[]> = {
	device: ["mobile", "desktop"],
	traffic: ["organic_search", "social", "email", "direct", "referral", "paid"],
	funnel: ["new", "browsing", "engaged", "applied_today", "signed_up_free"],
	page_family: ["job", "jobs", "hunt", "blog", "dashboard", "other"],
	market: ["ID", "SEA", "other"],
	segment: ["anon_shallow", "anon_deep", "free_shallow", "free_deep"],
};

/** Value used when a dimension is absent or outside its closed set. */
export const UNKNOWN_DIMENSION_VALUE = "unknown";

/**
 * Query-string names the client uses for each client-side dimension.
 * `market` is resolved server-side from the request's geo, never sent.
 */
export const CONTEXT_QUERY_PARAMS: Record<
	Exclude<TargetingDimension, "market">,
	string
> = {
	device: "cx_device",
	traffic: "cx_traffic",
	funnel: "cx_funnel",
	page_family: "cx_page",
	segment: "cx_segment",
};

/** Per-arm unclicked-view counts since the last click: `wise:5,italki:2`. */
export const FATIGUE_QUERY_PARAM = "cx_seen";
/** Per-arm hours since the visitor last clicked it: `wise:3.5`. */
export const CLICKED_QUERY_PARAM = "cx_clicked";
